import { Container, logError } from '@utils-global';
import { routes } from '../constants';
import { withGraphRetry } from './helpers';

type TranslateExchangeIdsResponse = {
  value?: { sourceId: string; targetId: string }[];
};

/**
 * Which id the caller ended up with. The fallback is silent by design, so the kind
 * is the only way a later move or delete failure can be attributed to the id it ran
 * against rather than to the translation.
 */
export type ImmutableMailIdKind = 'immutable' | 'mutable-fallback';

export type ResolvedImmutableMailId = {
  id: string;
  kind: ImmutableMailIdKind;
};

/**
 * Translates a mutable Graph message id into an immutable one, which survives
 * the item being moved between folders. Must be called while the source id is
 * still valid (before the item is relocated). Transient failures get the same
 * retries as the move/delete they feed; a permanent failure falls back to the
 * original id, so reporting is never blocked.
 * https://learn.microsoft.com/graph/outlook-immutable-id
 */
export const getImmutableMailId = async (
  mailId: string,
  userEmail: string,
  isSharedMailbox: boolean = false,
): Promise<ResolvedImmutableMailId> => {
  try {
    const res = await withGraphRetry(
      () =>
        Container.graphClient.post(routes.office.translateExchangeIds(userEmail), {
          inputIds: [mailId],
          sourceIdType: 'restId',
          targetIdType: 'restImmutableEntryId',
        }),
      { isSharedMailbox },
    );

    const { value } = (res?.data ?? {}) as TranslateExchangeIdsResponse;
    const immutableId = value?.[0]?.targetId;

    // A response without a target id is as much a fallback as a thrown failure,
    // the caller still walks away holding the mutable id.
    return immutableId
      ? { id: immutableId, kind: 'immutable' }
      : { id: mailId, kind: 'mutable-fallback' };
  } catch (error) {
    logError('Failed to translate to immutable mail id, using the mutable id', error);
    return { id: mailId, kind: 'mutable-fallback' };
  }
};
